/**
 * Online medicine ordering: clinic settings, the in-house pharmacy's orders board, delivery hand-over,
 * and the patient ordering flow. The clinic is always the active clinic (staff) or the patient's token
 * clinic — never sent by us. Field names mirror the backend records in MedicineOrderDtos.
 */
import { fetchApi, fetchPatientApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export type MedicineOrderStatus =
  | 'SENT' | 'VIEWED' | 'PREPARED' | 'PACKED' | 'OUT_FOR_DELIVERY' | 'DELIVERED' | 'COLLECTED' | 'CANCELLED';
export type Fulfilment = 'PICKUP' | 'DELIVERY';
/** Legal sale category of a stock item; null = not classified yet. */
export type DrugSchedule = 'OTC' | 'H' | 'H1' | 'X' | 'NDPS';

export interface MoPharmacyOption { id: string; name: string }

export interface MoSettings {
  entitled: boolean;
  enabled: boolean;
  pharmacyId?: string | null;
  pharmacies: MoPharmacyOption[];
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  deliveryFee: number;
  gstPercent: number;
  pincodes: string[];
  prescriptionValidityDays: number;
  patientInstructions?: string | null;
  setupProblems: string[];
}

export interface MoSettingsInput {
  enabled?: boolean;
  pharmacyId?: string;
  pickupEnabled?: boolean;
  deliveryEnabled?: boolean;
  deliveryFee?: number;
  gstPercent?: number;
  pincodes?: string[];
  prescriptionValidityDays?: number;
  patientInstructions?: string;
}

export interface MoBill { id: string; billNumber: string; total: number; paymentStatus: string }

// ─── Pharmacy / staff ────────────────────────────────────────────────────────────────────────

export interface MoStockOption {
  pharmacyMedicationId: string;
  name: string;
  strength?: string | null;
  drugSchedule?: DrugSchedule | null;
  currentQuantity: number;
  unitPrice?: number | null;
  expiryDate?: string | null;
  expired: boolean;
  exact: boolean;
}

export interface MoStaffItem {
  id: string;
  prescriptionMedicationId: string;
  prescribedName: string;
  dosage?: string | null;
  frequency?: string | null;
  duration?: string | null;
  requestedQuantity?: number | null;
  decision: 'PENDING' | 'INCLUDED' | 'EXCLUDED';
  pharmacyMedicationId?: string | null;
  dispensedName?: string | null;
  substituted: boolean;
  drugSchedule?: DrugSchedule | null;
  dispensedQuantity?: number | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
  exclusionReason?: string | null;
  /** Only when the order was opened (stock choices for verification). */
  options?: MoStockOption[] | null;
}

export interface MoStaffOrder {
  id: string;
  orderNumber: string;
  status: MedicineOrderStatus;
  fulfilment: Fulfilment;
  createdAt?: string;
  patientName?: string | null;
  contactMobile?: string | null;
  addressLine?: string | null;
  landmark?: string | null;
  pincode?: string | null;
  patientNotes?: string | null;
  prescriptionId: string;
  prescriptionIssuedOn?: string | null;
  doctorName?: string | null;
  items: MoStaffItem[];
  deliveryFee: number;
  bill?: MoBill | null;
  verifiedByName?: string | null;
  pharmacistNotes?: string | null;
  deliveryAgentUserId?: string | null;
  deliveryAgentName?: string | null;
  failedDeliveries: number;
  lastFailedReason?: string | null;
  handoverFailedAttempts: number;
  cancelReason?: string | null;
}

export interface MoVerifyLine {
  itemId: string;
  include: boolean;
  pharmacyMedicationId?: string;
  quantity?: number;
  /** Classifies the chosen stock item when it hasn't been classified yet. */
  drugSchedule?: DrugSchedule;
  exclusionReason?: string;
}

export interface MoAgent { userId: string; name: string; role?: string | null }

// ─── Patient ─────────────────────────────────────────────────────────────────────────────────

export interface MoAvailability {
  offered: boolean;
  reason?: string | null;
  pickup: boolean;
  delivery: boolean;
  deliveryFee?: number | null;
  instructions?: string | null;
  pharmacyName?: string | null;
  prescriptionValidityDays: number;
  deliversToPincode?: boolean | null;
}

export type MoStockHint = 'IN_STOCK' | 'SUBSTITUTE_AVAILABLE' | 'PHARMACIST_TO_CONFIRM';

export interface MoOrderableMedicine {
  id: string;
  name: string;
  dosage?: string | null;
  frequency?: string | null;
  duration?: string | null;
  orderable: boolean;
  deliverable: boolean;
  stock: MoStockHint;
  note?: string | null;
}

export interface MoOrderablePrescription {
  id: string;
  issuedOn: string;
  doctorName?: string | null;
  orderableUntil: string;
  orderable: boolean;
  reason?: string | null;
  existingOrderNumber?: string | null;
  medicines: MoOrderableMedicine[];
}

export interface MoPlaceOrder {
  prescriptionId: string;
  fulfilment: Fulfilment;
  items: Array<{ prescriptionMedicationId: string; quantity?: number }>;
  address?: { line: string; landmark?: string; pincode: string };
  contactMobile: string;
  notes?: string;
}

export interface MoPatientItem {
  prescribedName: string;
  dispensedName?: string | null;
  substituted: boolean;
  quantity?: number | null;
  lineTotal?: number | null;
  decision: 'PENDING' | 'INCLUDED' | 'EXCLUDED';
  exclusionReason?: string | null;
}

export interface MoPatientOrder {
  id: string;
  orderNumber: string;
  status: MedicineOrderStatus;
  fulfilment: Fulfilment;
  pharmacyName?: string | null;
  pincode?: string | null;
  items: MoPatientItem[];
  deliveryFee: number;
  bill?: MoBill | null;
  cancellable: boolean;
  /** Present only while it's needed: ready for pickup, or out for delivery. */
  handoverCode?: string | null;
  cancelReason?: string | null;
  createdAt?: string;
}

const post = (body?: unknown): RequestInit => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export const medicineOrdersModule = {
  settings: {
    get: () => fetchApi<MoSettings>('/api/medicine-ordering/settings'),
    update: (data: MoSettingsInput) => fetchApi<MoSettings>('/api/medicine-ordering/settings', { method: 'PUT', body: JSON.stringify(data) }),
  },
  board: (finished = false) => fetchApi<MoStaffOrder[]>(`/api/medicine-orders?finished=${finished}`),
  /** One page of finished orders, most recently updated first (medopd-docs 112). */
  finishedPage: (page: number, size: number) =>
    fetchApi<PagedResponse<MoStaffOrder>>(`/api/medicine-orders?finished=true&page=${page}&size=${size}`),
  myDeliveries: () => fetchApi<MoStaffOrder[]>('/api/medicine-orders/my-deliveries'),
  agents: () => fetchApi<MoAgent[]>('/api/medicine-orders/delivery-agents'),
  /** Opens one order with stock choices; a pharmacist opening a new order marks it Viewed. */
  open: (id: string) => fetchApi<MoStaffOrder>(`/api/medicine-orders/${encodeURIComponent(id)}/open`, post()),
  verify: (id: string, lines: MoVerifyLine[], pharmacistNotes?: string) =>
    fetchApi<MoStaffOrder>(`/api/medicine-orders/${encodeURIComponent(id)}/verify`, post({ lines, pharmacistNotes })),
  pack: (id: string) => fetchApi<MoStaffOrder>(`/api/medicine-orders/${encodeURIComponent(id)}/pack`, post()),
  dispatch: (id: string, deliveryAgentUserId: string) => fetchApi<MoStaffOrder>(`/api/medicine-orders/${encodeURIComponent(id)}/dispatch`, post({ deliveryAgentUserId })),
  handOver: (id: string, code: string) => fetchApi<MoStaffOrder>(`/api/medicine-orders/${encodeURIComponent(id)}/hand-over`, post({ code })),
  deliveryFailed: (id: string, reason: string) => fetchApi<MoStaffOrder>(`/api/medicine-orders/${encodeURIComponent(id)}/delivery-failed`, post({ reason })),
  cancel: (id: string, reason: string) => fetchApi<MoStaffOrder>(`/api/medicine-orders/${encodeURIComponent(id)}/cancel`, post({ reason })),
};

/** Patient portal calls (patient token). */
export const patientMedicineOrdersModule = {
  availability: (pincode?: string) =>
    fetchPatientApi<MoAvailability>(`/api/patient/medicine-orders/availability${pincode ? `?pincode=${encodeURIComponent(pincode)}` : ''}`),
  prescriptions: () => fetchPatientApi<MoOrderablePrescription[]>('/api/patient/medicine-orders/prescriptions'),
  orders: () => fetchPatientApi<MoPatientOrder[]>('/api/patient/medicine-orders'),
  /** One page of issued prescriptions, newest first; the unpaged call stops at 20 (medopd-docs 117). */
  prescriptionsPage: (page: number, size: number) =>
    fetchPatientApi<PagedResponse<MoOrderablePrescription>>(`/api/patient/medicine-orders/prescriptions?page=${page}&size=${size}`),
  /** One page of the patient's orders, newest first (medopd-docs 117). */
  ordersPage: (page: number, size: number) =>
    fetchPatientApi<PagedResponse<MoPatientOrder>>(`/api/patient/medicine-orders?page=${page}&size=${size}`),
  place: (data: MoPlaceOrder) => fetchPatientApi<MoPatientOrder>('/api/patient/medicine-orders', post(data)),
  cancel: (id: string, reason?: string) =>
    fetchPatientApi<MoPatientOrder>(`/api/patient/medicine-orders/${encodeURIComponent(id)}/cancel`, post({ reason })),
};
